import axios, { AxiosResponse } from 'axios';
import { CalibratedSite } from 'views/bluviu-nutrition/features/scenarios/types/calibrated-site.type';
import { CloneScenarioRequest } from 'views/bluviu-nutrition/features/scenarios/types/clone-scenario-request.type';
import { CreateScenarioRequest } from 'views/bluviu-nutrition/features/scenarios/types/create-scenario-request.type';
import { ScenarioFeed } from 'views/bluviu-nutrition/features/scenarios/types/scenario-feed.type';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios/types/scenario-list-item.type';
import { ScenarioResult, SummaryType } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';
import { Scenario } from 'views/bluviu-nutrition/features/scenarios/types/scenario.type';
import { UpdateScenarioFeedProgramRequest } from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-feed-program-request.type';
import { UpdateScenarioRequest } from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-input.type';
import { FilterFeed } from 'views/bluviu-nutrition/types/filter-feed.enum';

/**
 * Returns a list of calibrated sites the user has access to for a company
 * @param globalCompanyId
 * @returns `CalibratedSite[]`
 */
export const getCalibratedSites = async (globalCompanyId: string): Promise<CalibratedSite[]> => {
  const res = await axios.get<CalibratedSite[]>(`/ewosapi/v1/scenario/gt2/sites?globalCompanyId=${globalCompanyId}`);
  return res.data;
};

/**
 * /ewosapi/v1/scenario/gt2/{globalSiteId}
 * Get a list of scenarios belonging to a company with the specified `globalCompanyId`
 * @param globalSiteId
 * @returns `ScenarioListItem[]`
 */
export const getScenarios = async (globalSiteId: string): Promise<ScenarioListItem[]> => {
  const res = await axios.get<ScenarioListItem[]>(`/ewosapi/v1/scenario/gt2/${globalSiteId}`);
  return res.data;
};

/**
 * Add a Scenario
 * @param globalCompanyId
 * @param request
 * @returns `Scenario`
 */
export const postScenario = async (globalCompanyId: string, request: CreateScenarioRequest): Promise<Scenario> => {
  console.log(request);

  const res = await axios.post<Scenario>(`/ewosapi/v1/scenario/gt2/scenario?globalCompanyId=${globalCompanyId}`, request);
  return res.data;
};

/**
 * Gets a single scenario belonging to a company based on the `globalCompanyId` and `scenarioId`
 * @param globalCompanyId
 * @param scenarioId
 * @returns `Scenario`
 */
export const getScenario = async (globalCompanyId: string, scenarioId: string): Promise<Scenario> => {
  const res = await axios.get<Scenario>(
    `/ewosapi/v1/scenario/gt2/scenario?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`
  );
  return res.data;
};

/**
 * Updates a scenario belonging to a company based on `globalCompanyId` and `scenarioId`
 * @param globalCompanyId
 * @param scenarioId
 * @param request `UpdateScenarioInput`
 * @returns `Scenario`
 */
export const putScenario = async (globalCompanyId: string, scenarioId: string, request: UpdateScenarioRequest): Promise<Scenario> => {
  const res = await axios.put<Scenario>(
    `/ewosapi/v1/scenario/gt2/scenario?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`,
    request
  );
  return res.data;
};

/**
 * Deletes a scenario belonging to a company based on `globalCompanyId` and `scenarioId`
 * @param index Used to track the corresponding dialog to close upon deletion success. Could possibly be replaced with the `scenarioId`
 * @param globalCompanyId
 * @param scenarioId
 * @returns `number`
 */
export const deleteScenario = async (globalCompanyId: string, scenarioId: string): Promise<AxiosResponse> => {
  const res = await axios.delete<AxiosResponse>(`/ewosapi/v1/scenario?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`);

  return res.data;
};

/**
 * Update the calibration feed program for a site
 * @param globalCompanyId
 * @param scenarioId
 * @param request
 * @returns
 */
export const putScenarioFeedProgram = async (
  globalCompanyId: string,
  scenarioId: string,
  request: UpdateScenarioFeedProgramRequest
): Promise<Scenario> => {
  const res = await axios.put<Scenario>(
    `/ewosapi/v1/scenario/gt2/feed-programs?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`,
    request
  );
  return res.data;
};

/**
 * Clone an existing scenario belonging to a company based on `globalCompanyId` and `scenarioId`
 * @param globalCompanyId
 * @param scenarioId
 * @param request `CreateScenarioInput`
 * @returns
 */
export const cloneScenario = async (globalCompanyId: string, scenarioId: string, request: CloneScenarioRequest): Promise<Scenario> => {
  const res = await axios.post<Scenario>(
    `/ewosapi/v1/scenario/gt2/clone?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`,
    request
  );
  return res.data;
};

/**
 * Run a scenario for computation
 * @param globalCompanyId
 * @param scenarioId
 * @returns
 */
export const runScenario = async (globalCompanyId: string, scenarioId: string): Promise<AxiosResponse> => {
  const res = await axios.put<AxiosResponse>(
    `/ewosapi/v1/scenario/gt2/run?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`
  );
  return res;
};

/**
 * Get a scenario summary after a scenario has been run and the results have been calculated
 * @param globalCompanyId
 * @param scenarioId
 * @param summaryType `SummaryType`
 * @param target `Number`
 * @returns
 */
export const getScenarioSummary = async (
  globalCompanyId: string,
  scenarioId: string,
  summaryType: SummaryType,
  target?: number
): Promise<ScenarioResult> => {
  const optional = !target ? `` : `&${summaryType}=${target}`;

  const res = await axios.get<ScenarioResult>(
    `/ewosapi/v1/scenario/gt2/summary?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}&type=${summaryType}${optional}`
  );
  return res.data;
};

/**
 * Returns active feeds for a scenario
 * @param globalCompanyId
 * @returns
 */
export const getManualFeeds = async (globalCompanyId: string, filter: FilterFeed): Promise<ScenarioFeed[]> => {
  const res = await axios.get<ScenarioFeed[]>(`/ewosapi/v1/feeds/manual/company/${globalCompanyId}/feed?filter=${filter}`);
  return res.data;
};

/**
 * Get the input data for a specific Scenario. Requires SuperUser
 * @param globalCompanyId
 * @param scenarioId
 * @returns `blob`
 */
export const getScenarioInputFile = async (globalCompanyId: string, scenarioId: string): Promise<AxiosResponse> => {
  return await axios.get<AxiosResponse>(
    `ewosapi/v1/scenario/gt2/file/input?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`,
    {
      responseType: 'blob',
    }
  );
};

/**
 * Gets the output data for a specific Scenario. Requires SuperUser
 * @param globalCompanyId
 * @param scenarioId
 * @returns `blob`
 */
export const getScenarioOutputFile = async (globalCompanyId: string, scenarioId: string): Promise<AxiosResponse> => {
  return await axios.get<AxiosResponse>(
    `/ewosapi/v1/scenario/gt2/file/output?globalCompanyId=${globalCompanyId}&scenarioId=${scenarioId}`,
    {
      responseType: 'blob',
    }
  );
};
